import { ShieldCheck, Lock, FileLock2 } from "lucide-react";

/**
 * Short, careful cybersecurity reassurance block surfaced on the public
 * site, member portal, and KYC pages. Wording matches the user's
 * compliance-conscious brief — NO absolute claims like "bank-grade" or
 * "unhackable". Confidence-building, not boastful.
 *
 * Variants:
 *   - "homepage": single one-liner under the hero subtitle.
 *   - "portal":   2-3 sentence block for the member portal/dashboard.
 *   - "kyc":      one-liner for the KYC/accreditation upload page.
 *   - "footer":   single sentence for the footer bar.
 */
const SecurityAssurance = ({ variant = "homepage", className = "" }) => {
  if (variant === "footer") {
    return (
      <p
        className={`text-platinum/45 text-xs ${className}`}
        data-testid="security-assurance-footer"
      >
        <ShieldCheck className="inline-block mr-1.5 mb-0.5 text-gold/70" size={11} />
        Cybersecurity, privacy, and secure onboarding are core priorities of the EchoSense platform.
      </p>
    );
  }

  if (variant === "kyc") {
    return (
      <div
        className={`flex items-start gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/25 ${className}`}
        data-testid="security-assurance-kyc"
      >
        <FileLock2 className="text-emerald-400 flex-shrink-0 mt-0.5" size={18} />
        <p className="text-platinum/85 text-sm leading-relaxed">
          EchoSense supports secure submission workflows for identity verification,
          accreditation documentation, and member onboarding. Documents are transmitted over
          encrypted HTTPS connections and stored in role-restricted compliance storage.
        </p>
      </div>
    );
  }

  if (variant === "portal") {
    return (
      <div
        className={`flex items-start gap-3 p-4 rounded-lg bg-surface border border-gold/20 ${className}`}
        data-testid="security-assurance-portal"
      >
        <ShieldCheck className="text-gold flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-cream font-semibold text-sm mb-1">Secure, Step-by-Step Onboarding</p>
          <p className="text-platinum/75 text-sm leading-relaxed">
            Members are guided through a secure, step-by-step onboarding process designed to make
            identity verification, accreditation review, and document submission simple and
            protected. Account access uses encrypted authentication; activity is logged for compliance review.
          </p>
        </div>
      </div>
    );
  }

  // Default: "homepage"
  return (
    <div
      className={`flex items-center gap-2.5 text-platinum/75 text-sm ${className}`}
      data-testid="security-assurance-homepage"
    >
      <Lock className="text-gold flex-shrink-0" size={16} />
      <span>
        Secure onboarding, privacy-conscious workflows, and cybersecurity-aware platform design.
      </span>
    </div>
  );
};

export default SecurityAssurance;
